import React, { useEffect } from 'react'
import Input from '../../components/elements/inputs/Input'
import { useTranslation } from 'next-i18next'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import Button from '../../components/elements/Button'
import { signIn, useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import { routes } from '../../utils/routes'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import AuthLayout from '../../layouts/AuthLayout'
import Head from 'next/head'
import { useOrganization } from '../../hooks/useOrganization'
import OnboardingImage from '../../components/elements/OnboardingImage'

type IFormData = {
    email: string
    password: string
}

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale ? ctx.locale : '', ['common', 'signin', 'meta'])),
        },
    }
}

const Inloggen = () => {
    const { t } = useTranslation(['common', 'signin'])
    const { organization } = useOrganization()
    const nextRouter = useRouter()
    const { redirect } = nextRouter.query

    const authMethod = process.env.NEXT_PUBLIC_AUTH_METHOD || 'CREDENTIALS'
    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IFormData>({
        mode: 'onTouched',
    })

    const router = useRouter()
    const { status } = useSession()

    useEffect(() => {
        if (status === 'authenticated') {
            void router.replace(typeof redirect === 'string' ? redirect : '/')
        }
    }, [status])

    const onSubmit = async (data: IFormData) => {
        if (isSubmitting) return

        try {
            const id = toast.loading(<>{t('signing-in', { ns: 'signin' })}</>, { toastId: 'signing-in-toast' })
            const res = await signIn('credentials', {
                email: data.email,
                password: data.password,
                callbackUrl: '/',
                redirect: false,
            })
            if (res) {
                if (res.status === 200) {
                    toast.update(id, {
                        render: <>{t('signing-in-success', { ns: 'signin' })}</>,
                        type: 'success',
                        isLoading: false,
                        autoClose: 3000,
                        closeOnClick: true,
                        closeButton: true,
                        toastId: 'signing-in-toast',
                    })
                } else {
                    toast.update(id, {
                        render: <>{t('signing-in-error', { ns: 'signin' })}</>,
                        type: 'error',
                        isLoading: false,
                        autoClose: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        closeButton: true,
                        toastId: 'signing-in-toast',
                    })
                }
            }
        } catch (e) {
            console.log('error', e)
        }
        // })()
    }

    const OIDCLogin = () => {
        window.location.href = process.env.NEXT_PUBLIC_API_URL + '/account/oidc/login'
    }

    if (authMethod === 'OIDC-AND-CREDENTIALS')
        return (
            <AuthLayout>
                <main className="min-h-screen lg:overflow-hidden">
                    <div className="flex min-h-screen">
                        <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                            <OnboardingImage />
                        </div>
                        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                            <div className="mx-auto w-full max-w-sm lg:w-96">
                                <div>
                                    <h2>{t('sign-in')}</h2>
                                    <p className="mt-4">
                                        {t('description-oidc-and-credentials', {
                                            organization: organization?.name,
                                            ns: 'signin',
                                        })}{' '}
                                    </p>
                                    {/*<p className="mt-4 ">{t('description-oidc-question', { ns: 'signin' })} </p>*/}
                                </div>

                                <div className="border border-gray-200 bg-gray-50 rounded-lg px-8 py-8 shadow-sm mt-4">
                                    <div className="">
                                        <div>
                                            {/*<form className="space-y-6" onSubmit={() => OIDCLogin()} noValidate={true}>*/}
                                            <div>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => OIDCLogin()}
                                                    className="w-full"
                                                >
                                                    {t('sign-in-oidc-default', { ns: 'signin' })}
                                                </Button>
                                            </div>
                                            {/*</form>*/}
                                        </div>
                                    </div>

                                    <div className="relative mt-8">
                                        <div aria-hidden="true" className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-200" />
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="bg-gray-50 px-6 ">of log in met</span>
                                        </div>
                                    </div>

                                    <div className="mt-8">
                                        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate={true}>
                                            <div className="relative">
                                                <Controller
                                                    name="email"
                                                    rules={{
                                                        required: t('email-required', { ns: 'signin' }),
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: t('email-invalid', { ns: 'signin' }),
                                                        },
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('email-address')}
                                                            type="email"
                                                            placeholder={t('email-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />
                                                {errors.email && (
                                                    <p
                                                        className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.email?.message}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Controller
                                                    name="password"
                                                    rules={{
                                                        required: t('password-required', { ns: 'signin' }),
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('password')}
                                                            type="password"
                                                            placeholder={t('password-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />

                                                {errors.password && (
                                                    <p
                                                        className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.password?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex items-center justify-between gap-4">
                                                <Button variant="secondary" className="w-full" type="submit">
                                                    {t('sign-in')}
                                                </Button>
                                                <div className="text-xs shrink">
                                                    <Link href={routes.trouble.path}>
                                                        <a href={routes.trouble.path}>
                                                            {t('troubles-short', { ns: 'signin' })}
                                                        </a>
                                                    </Link>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="">
                                        {t('no-account', {
                                            organization: organization?.name,
                                            organizationURL: organization?.url,
                                            ns: 'signin',
                                        })}{' '}
                                        <Link href={routes.register.path}>
                                            <a href={routes.register.path}>
                                                {t('register-first-link', { ns: 'signin' })}
                                            </a>
                                        </Link>
                                    </p>
                                </div>

                                <div className="pt-8">
                                    <p className=" text-xs leading-tight text-center text-gray-400 ">
                                        De Digitale Academie Noord-Nederland wordt mede mogelijk gemaakt door Nationaal
                                        Programma Groningen, Hanze, Rijksuniversiteit Groningen, Noorderpoort, NHL
                                        Stenden en Biblionet Groniningen.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </AuthLayout>
        )

    if (authMethod === 'OIDC')
        return (
            <AuthLayout>
                <main className="min-h-screen lg:overflow-hidden">
                    <div className="flex min-h-screen">
                        <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                            <OnboardingImage />
                        </div>
                        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                            <div className="mx-auto w-full max-w-sm lg:w-96">
                                <div>
                                    <h2>{t('sign-in')}</h2>
                                    <p className="mt-4">
                                        {t('description-oidc', { ns: 'signin' })}{' '}
                                        <a href="https://leren.dann.nl">leren.dann.nl</a>.
                                    </p>
                                    <p className="mt-4">{t('description-oidc-question', { ns: 'signin' })} </p>
                                </div>

                                <div className="mt-8">
                                    <div>
                                        {/*<form className="space-y-6" onSubmit={() => OIDCLogin()} noValidate={true}>*/}
                                        <div>
                                            <Button
                                                variant="primary"
                                                onClick={() => OIDCLogin()}
                                                className="w-full mt-4"
                                            >
                                                {t('sign-in')}
                                            </Button>
                                        </div>
                                        {/*</form>*/}
                                    </div>
                                </div>

                                <div className="pt-8">
                                    <p className=" text-xs leading-tight text-center text-gray-400 ">
                                        De Digitale Academie Noord-Nederland wordt mede mogelijk gemaakt door Nationaal
                                        Programma Groningen, Hanze, Rijksuniversiteit Groningen, Noorderpoort, NHL
                                        Stenden en Biblionet Groniningen.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </AuthLayout>
        )

    return (
        <>
            <Head>
                <title>{t('inloggen.title', { ns: 'meta' })}</title>
                <meta name="description" content={t('inloggen.description', { ns: 'meta' })} />
            </Head>
            <AuthLayout>
                <main className="min-h-screen lg:overflow-hidden">
                    <div className="flex min-h-screen">
                        <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                            <OnboardingImage />
                        </div>
                        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                            <div className="mx-auto w-full max-w-sm lg:w-96">
                                <div>
                                    <h2>{t('sign-in')}</h2>
                                    <p className="mt-4">
                                        {t('description', { ns: 'signin' })}
                                        {t('register-first', { ns: 'signin' })}{' '}
                                        <Link
                                            href={
                                                typeof redirect === 'string'
                                                    ? routes.register.path + '?redirect=' + redirect
                                                    : routes.register.path
                                            }
                                        >
                                            <a
                                                href={
                                                    typeof redirect === 'string'
                                                        ? routes.register.path + '?redirect=' + redirect
                                                        : routes.register.path
                                                }
                                            >
                                                {' '}
                                                {t('register-first-link', { ns: 'signin' })}
                                            </a>
                                        </Link>
                                    </p>
                                </div>

                                <div className="mt-8">
                                    <div>
                                        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate={true}>
                                            <div className="relative">
                                                <Controller
                                                    name="email"
                                                    rules={{
                                                        required: t('email-required', { ns: 'signin' }),
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: t('email-invalid', { ns: 'signin' }),
                                                        },
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('email-address')}
                                                            type="email"
                                                            placeholder={t('email-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />
                                                {errors.email && (
                                                    <p
                                                        className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.email?.message}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Controller
                                                    name="password"
                                                    rules={{
                                                        required: t('password-required', { ns: 'signin' }),
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('password')}
                                                            type="password"
                                                            placeholder={t('password-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />

                                                {errors.password && (
                                                    <p
                                                        className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.password?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <Button variant="primary" className="w-full mt-4" type="submit">
                                                    {t('sign-in')}
                                                </Button>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="text-sm">
                                                    <Link href={routes.trouble.path}>
                                                        <a href={routes.trouble.path}>
                                                            {t('troubles', { ns: 'signin' })}
                                                        </a>
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className="pt-8">
                                                <p className=" text-xs leading-tight text-center text-gray-400 ">
                                                    De Digitale Academie Noord-Nederland wordt mede mogelijk gemaakt
                                                    door Nationaal Programma Groningen, Hanze, Rijksuniversiteit
                                                    Groningen, Noorderpoort, NHL Stenden en Biblionet Groniningen.
                                                </p>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </AuthLayout>
        </>
    )
}

export default Inloggen
